import { createElement } from 'react';
import { lazyWithRetry } from '@app/utils/loadable';
import { redirect } from 'react-router-dom';
import AppLayout from '@app/layouts/AppLayout';
import InitialDataGate from '@app/containers/InitialDataGate';
import MinimalLayout from '@app/layouts/MinimalLayout';
import AuthenticatedShell from '@app/containers/AuthenticatedShell';
import LoginCallbackHandler from '@app/components/LoginCallbackHandler';
import RootErrorBoundary from '@app/components/RootErrorBoundary';

const HomePage = lazyWithRetry(() => import('@app/containers/HomePage'));
const DefaultLandingRedirect = lazyWithRetry(() =>
  import('@app/containers/App/DefaultLandingRedirect'),
);
const MessagesFlag = lazyWithRetry(() => import('@app/containers/Messages'));
const MessagesList = lazyWithRetry(() => import('@app/containers/Messages/List'));
const MessagesDetail = lazyWithRetry(() => import('@app/containers/Messages/Detail'));
const MessagesNew = lazyWithRetry(() => import('@app/containers/Messages/New'));
const DeductiblesDetailsPage = lazyWithRetry(() => import('@app/containers/DeductiblesDetailsPage'));
const EnrollmentPage = lazyWithRetry(() => import('@app/containers/EnrollmentPage'));
const MDVSubpage = lazyWithRetry(() => import('@app/containers/MDVSubpage'));
const MarketplacePage = lazyWithRetry(() => import('@app/containers/MarketplacePage'));
const MarketplaceList = lazyWithRetry(() => import('@app/containers/MarketplacePage/MarketplaceList'));
const MarketplaceFlagRoute = lazyWithRetry(() => import('@app/containers/MarketplacePage/MarketplaceFlagRoute'));
const ResendActivationEmail = lazyWithRetry(() => import('@app/containers/Auth/ResendActivationEmail'));
const CreateAccount = lazyWithRetry(() => import('@app/containers/Auth/CreateAccount'));
const FindCareRoutes = lazyWithRetry(() => import('@app/containers/FindCareRoutes'));
const TelehealthPage = lazyWithRetry(() => import('@app/containers/TelehealthPage'));
const DocumentsPage = lazyWithRetry(() => import('@app/containers/AccountRoutes/DocumentsPage'));
const ProfilePage = lazyWithRetry(() => import('@app/containers/AccountRoutes/ProfilePage'));
const SupportPage = lazyWithRetry(() => import('@app/containers/AccountRoutes/SupportPage'));
const AccountPage = lazyWithRetry(() => import('@app/containers/AccountRoutes/AccountPage'));
const AdminRoutes = lazyWithRetry(() => import('@app/containers/AdminRoutes'));
const AdminPage = lazyWithRetry(() => import('@app/containers/AdminRoutes/AdminPage'));
const PerksPage = lazyWithRetry(() => import('@app/containers/AdminRoutes/PerksPage'));
const ManageUsersPage = lazyWithRetry(() => import('@app/containers/AdminRoutes/ManageUsersPage'));
const AdminPerkDetail = lazyWithRetry(() => import('@app/containers/AdminRoutes/AdminPerkDetail'));
const ExportPage = lazyWithRetry(() => import('@app/containers/AdminRoutes/ExportPage'));
const MFAPage = lazyWithRetry(() => import('@app/containers/AdminRoutes/MFAPage'));
const RipplingOauth = lazyWithRetry(() => import('@app/containers/RipplingOauth'));
const PerksRoutesFlag = lazyWithRetry(() => import('@app/containers/PerksRoutes'));
const PerkDetailPage = lazyWithRetry(() => import('@app/containers/PerkDetailPage'));
const Logout = lazyWithRetry(() => import('@app/containers/LogoutPage'));
const AuthFailurePage = lazyWithRetry(() => import('@app/containers/AuthFailurePage'));
const UserDetailsPage = lazyWithRetry(() => import('@app/containers/AdminRoutes/UserDetailsPage'));
const InsightsFlag = lazyWithRetry(() => import('@app/containers/InsightsRoutes'));
const InsightsPage = lazyWithRetry(() => import('@app/containers/AdminRoutes/InsightsPage'));
const SuperAdminRoutes = lazyWithRetry(() => import('@app/containers/SuperAdmin'));

export const createRoutes = ({ authLoader }) => [
  {
    path: '/signin/resend-activation-email',
    Component: ResendActivationEmail,
    errorElement: createElement(RootErrorBoundary),
  },
  {
    path: '/create-account',
    Component: CreateAccount,
    errorElement: createElement(RootErrorBoundary),
  },
  {
    path: '/implicit/callback',
    Component: LoginCallbackHandler,
    errorElement: createElement(RootErrorBoundary),
  },
  {
    path: '/',
    Component: AuthenticatedShell,
    loader: authLoader,
    errorElement: createElement(RootErrorBoundary),
    children: [
      {
        id: 'shell-minimal-impersonate',
        path: 'impersonate',
        Component: MinimalLayout,
        children: [
          {
            index: true,
            Component: SuperAdminRoutes,
          },
        ],
      },
      {
        id: 'shell-minimal-logout',
        path: 'logout',
        Component: MinimalLayout,
        children: [
          {
            index: true,
            Component: Logout,
          },
        ],
      },
      {
        id: 'shell-minimal-auth-failure',
        path: 'auth-failure',
        Component: MinimalLayout,
        children: [
          {
            index: true,
            Component: AuthFailurePage,
          },
        ],
      },
      {
        id: 'shell-minimal-rippling',
        path: 'integrations/rippling',
        Component: MinimalLayout,
        children: [
          {
            index: true,
            Component: RipplingOauth,
          },
        ],
      },
      {
        Component: InitialDataGate,
        children: [
          {
            id: 'shell-app-layout',
            Component: AppLayout,
            children: [
              {
                index: true,
                Component: DefaultLandingRedirect,
              },
              {
                path: 'dashboard',
                Component: HomePage,
              },
              {
                path: 'telehealth',
                Component: TelehealthPage,
              },
              {
                path: 'marketplace',
                Component: MarketplaceFlagRoute,
                children: [
                  {
                    path: 'v2/*',
                    loader: () => redirect('/marketplace'),
                  },
                  {
                    path: ':category?',
                    Component: MarketplacePage,
                    children: [
                      {
                        index: true,
                        Component: MarketplaceList,
                      },
                    ],
                  },
                  {
                    path: 'tabs/:tab?/:category?',
                    Component: MarketplacePage,
                    children: [
                      {
                        index: true,
                        Component: MarketplaceList,
                      },
                      {
                        path: ':category',
                        Component: MarketplaceList,
                      },
                    ],
                  },
                ],
              },
              {
                path: 'benefits/medical/deductibles-and-maximums',
                Component: DeductiblesDetailsPage,
              },
              {
                path: 'benefits/:bucket/:enrollment',
                Component: EnrollmentPage,
              },
              {
                path: 'benefits/:bucket/:enrollment/:subpage',
                Component: MDVSubpage,
              },
              {
                path: 'ask-bennie',
                Component: MessagesFlag,
                children: [
                  {
                    index: true,
                    Component: MessagesList,
                  },
                  {
                    path: 'tickets/:requestId',
                    Component: MessagesDetail,
                  },
                  {
                    path: 'new/scheduling',
                    loader: () => redirect('/ask-bennie'),
                  },
                  {
                    path: 'new/:topic',
                    Component: MessagesNew,
                  },
                ],
              },
              {
                path: 'find-a-doctor/*',
                Component: FindCareRoutes,
              },
              {
                path: 'find-medical-care/*',
                Component: FindCareRoutes,
              },
              {
                path: 'account',
                Component: AccountPage,
              },
              {
                path: 'account/profile',
                Component: ProfilePage,
              },
              {
                path: 'account/documents',
                Component: DocumentsPage,
              },
              {
                path: 'account/support',
                Component: SupportPage,
              },
              {
                path: 'admin',
                Component: AdminRoutes,
                children: [
                  {
                    index: true,
                    Component: AdminPage,
                  },
                  {
                    path: 'insights',
                    Component: InsightsFlag,
                    children: [
                      {
                        index: true,
                        Component: InsightsPage,
                      },
                    ],
                  },
                  {
                    path: 'manage-users',
                    children: [
                      {
                        index: true,
                        Component: ManageUsersPage,
                      },
                      {
                        path: ':userId',
                        Component: UserDetailsPage,
                      },
                    ],
                  },
                  {
                    path: 'perks',
                    Component: PerksRoutesFlag,
                    children: [
                      {
                        index: true,
                        Component: PerksPage,
                      },
                      {
                        path: ':perkId',
                        Component: AdminPerkDetail,
                      },
                    ],
                  },
                  {
                    path: 'export-data',
                    Component: ExportPage,
                  },
                  {
                    path: 'mfa',
                    Component: MFAPage,
                  },
                ],
              },
              {
                path: 'perks',
                Component: PerksRoutesFlag,
                children: [
                  {
                    index: true,
                    loader: () => redirect('/dashboard'),
                  },
                  {
                    path: ':perkId',
                    Component: PerkDetailPage,
                  },
                ],
              },
              {
                path: '*',
                loader: ({ request }) => {
                  const url = new URL(request.url);
                  import('@sentry/react').then(({ captureMessage }) => {
                    captureMessage('Unmatched route hit catch-all redirect', {
                      level: 'warning',
                      tags: { section: 'routing' },
                      extra: {
                        pathname: url.pathname,
                        search: url.search,
                      },
                    });
                  });
                  return redirect('/');
                },
              },
            ],
          },
        ],
      },
    ],
  },
];
